import React, { useState } from 'react';
import { 
  Globe, 
  Key, 
  Lock,
  Code,
  Send,
  FileJson,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Check,
  Copy,
  PlayCircle,
  Edit,
  Save,
  Plus,
  Trash,
  MessageSquare
} from 'lucide-react';

// Structure de données de la documentation API
const API_DOCUMENTATION = {
  metadata: {
    title: "Documentation API",
    version: "v1.0.0",
    baseUrl: "https://api.example.com/v1",
    lastUpdate: "2024-03-23",
    status: "stable"
  },
  authentication: {
    type: "Bearer Token",
    description: "Authentification via JWT Bearer Token",
    example: "Authorization: Bearer <your_token>",
    obtention: "Tokens are obtained through the /auth/login endpoint"
  },
  endpoints: [
    {
      id: "auth-login",
      category: "Authentification",
      path: "/auth/login",
      method: "POST",
      summary: "Authentification utilisateur",
      description: "Authentifie un utilisateur et retourne un token JWT",
      requestBody: {
        type: "application/json",
        schema: {
          email: "string",
          password: "string"
        },
        example: {
          email: "user@example.com",
          password: "password123"
        }
      },
      responses: [
        {
          status: 200,
          description: "Authentification réussie",
          schema: {
            token: "string",
            user: {
              id: "string",
              email: "string",
              role: "string"
            }
          },
          example: {
            token: "eyJhbGciOiJIUzI1NiIs...",
            user: {
              id: "123",
              email: "user@example.com",
              role: "user"
            }
          }
        },
        {
          status: 401,
          description: "Authentification échouée",
          schema: {
            error: "string"
          },
          example: {
            error: "Invalid credentials"
          }
        }
      ],
      security: ["None"],
      headers: ["Content-Type: application/json"]
    },
    {
      id: "users-profile",
      category: "Utilisateurs",
      path: "/users/profile",
      method: "GET",
      summary: "Profil utilisateur",
      description: "Récupère le profil de l'utilisateur connecté",
      responses: [
        {
          status: 200,
          description: "Profil récupéré avec succès",
          schema: {
            id: "string",
            email: "string",
            name: "string",
            role: "string",
            createdAt: "string"
          },
          example: {
            id: "123",
            email: "user@example.com",
            name: "John Doe",
            role: "user",
            createdAt: "2024-01-01T00:00:00Z"
          }
        },
        {
          status: 401,
          description: "Non authentifié",
          schema: {
            error: "string"
          }
        }
      ],
      security: ["Bearer Token"],
      headers: [
        "Authorization: Bearer <token>",
        "Content-Type: application/json"
      ]
    }
  ]
};

// Composants UI de base
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);

const Button = ({ 
  children, 
  className = '', 
  variant = 'default', 
  size = 'default',
  onClick,
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors';
  const variants = {
    default: 'bg-blue-500 text-white hover:bg-blue-600',
    outline: 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-700',
    ghost: 'hover:bg-gray-100 text-gray-700'
  };
  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-8 px-3 text-sm',
    lg: 'h-12 px-6'
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

// Composant Badge pour les méthodes HTTP
const MethodBadge = ({ method }) => {
  const colors = {
    GET: 'bg-green-100 text-green-800',
    POST: 'bg-blue-100 text-blue-800',
    PUT: 'bg-yellow-100 text-yellow-800',
    DELETE: 'bg-red-100 text-red-800',
    PATCH: 'bg-purple-100 text-purple-800'
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${colors[method]}`}>
      {method}
    </span>
  );
};

// Composant de visualisation JSON
const JsonViewer = ({ data, title }) => {
  const [isCopied, setIsCopied] = useState(false);
  const [testRequest, setTestRequest] = useState('');

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div className="relative">
      {title && (
        <div className="text-sm font-medium text-gray-700 mb-2">{title}</div>
      )}
      <div className="bg-gray-900 rounded-lg p-4 text-gray-100 font-mono text-sm overflow-x-auto">
        <pre>{JSON.stringify(data, null, 2)}</pre>
      </div>
      <button
        onClick={copyToClipboard}
        className="absolute top-2 right-2 p-2 rounded-md hover:bg-gray-700 text-gray-400 hover:text-gray-200"
      >
        {isCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
};

// Composant de documentation d'endpoint
const EndpointDoc = ({ endpoint, isEditing, onEdit }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [testRequest, setTestRequest] = useState(
    endpoint.requestBody ? JSON.stringify(endpoint.requestBody.example, null, 2) : ''
  );

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center space-x-4">
          <MethodBadge method={endpoint.method} />
          <div className="font-mono text-sm">{endpoint.path}</div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          {isExpanded ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
        </button>
      </CardHeader>

      {isExpanded && (
        <CardContent>
          <div className="space-y-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Description</h4>
              <p className="text-gray-600">{endpoint.description}</p>
            </div>

            {endpoint.requestBody && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Request Body</h4>
                <JsonViewer 
                  data={endpoint.requestBody.example} 
                  title="Example Request"
                />
              </div>
            )}

            <div>
              <h4 className="font-medium text-gray-900 mb-2">Responses</h4>
              <div className="space-y-4">
                {endpoint.responses.map((response, index) => (
                  <div key={index}>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                        response.status >= 200 && response.status < 300
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {response.status}
                      </span>
                      <span className="text-gray-600">{response.description}</span>
                    </div>
                    {response.example && (
                      <JsonViewer 
                        data={response.example} 
                        title="Example Response"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {endpoint.security && endpoint.security.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Sécurité</h4>
                <div className="flex space-x-2">
                  {endpoint.security.map((security, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium"
                    >
                      {security}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {endpoint.headers && endpoint.headers.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Headers requis</h4>
                <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm">
                  {endpoint.headers.map((header, index) => (
                    <div key={index}>{header}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Console de Test intégrée */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-4">Console de Test</h3>
              <div className="space-y-4">
                {endpoint.requestBody && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Corps de la requête
                    </label>
                    <textarea
                      rows={4}
                      className="w-full p-3 border rounded-lg font-mono text-sm"
                      value={testRequest}
                      onChange={(e) => setTestRequest(e.target.value)}
                    />
                  </div>
                )}
                <div className="flex justify-end">
                  <Button
                    className="flex items-center space-x-2"
                    onClick={() => {
                      console.log('Test request sent:', {
                        method: endpoint.method,
                        path: endpoint.path,
                        body: testRequest
                      });
                    }}
                  >
                    <PlayCircle className="h-4 w-4" />
                    <span>Tester</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Composant principal de documentation API
const DocumentationAPI = () => {
  const [apiData, setApiData] = useState(API_DOCUMENTATION);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', ...new Set(apiData.endpoints.map(e => e.category))];
  
  const filteredEndpoints = apiData.endpoints.filter(endpoint => 
    selectedCategory === 'all' || endpoint.category === selectedCategory
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* En-tête */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {apiData.metadata.title}
            </h1>
            <p className="mt-1 text-gray-600">
              Version {apiData.metadata.version} - Base URL: {apiData.metadata.baseUrl}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center space-x-2"
          >
            {isEditing ? (
              <>
                <Save className="h-4 w-4" />
                <span>Sauvegarder</span>
              </>
            ) : (
              <>
                <Edit className="h-4 w-4" />
                <span>Modifier</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Section Authentification */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Lock className="h-5 w-5 text-blue-500" />
            <span>Authentication</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-gray-900">Type</h4>
              <p className="text-gray-600">{apiData.authentication.type}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Description</h4>
              <p className="text-gray-600">{apiData.authentication.description}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Example</h4>
              <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm">
                {apiData.authentication.example}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtre par catégorie */}
      <div className="mb-6">
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                selectedCategory === category
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Liste des endpoints */}
      <div className="space-y-6">
        {filteredEndpoints.map((endpoint) => (
          <EndpointDoc
            key={endpoint.id}
            endpoint={endpoint}
            isEditing={isEditing}
            onEdit={(updated) => {
              const updatedEndpoints = apiData.endpoints.map(e =>
                e.id === updated.id ? updated : e
              );
              setApiData({ ...apiData, endpoints: updatedEndpoints });
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default DocumentationAPI;